/* eslint-disable lfds/no-raw-token-strings -- --lfds-skeleton-* are internal custom properties from #utils/skeleton.css, not design tokens */
import tokens from '@lansforsakringar/core-tokens'
import { css } from 'lit'

import { vars, scoped, declare, define } from '#utils/styles'
import { localsBoolean as booleanControl } from '#components/form/form-boolean/lfds-boolean.css.ts'

const locals = scoped('navigation-list-row', [
	'background_color',
	'border_color',
	'color',
	'color_secondary'
] as const)

export default css`
	:host {
		${declare(locals, {
			background_color: tokens.color_background_secondary,
			border_color: tokens.color_border_primary,
			color: tokens.color_text_primary,
			color_secondary: tokens.color_text_secondary
		})};

		display: block;
		width: 100%;
		container-type: inline-size;
	}

	a.row {
		text-decoration: none;
	}

	.row {
		display: flex;
		align-items: center;
		box-sizing: border-box;
		width: 100%;
		gap: ${vars(tokens.spacing_lg)};
		position: relative;
		border: none;
		border-bottom: 1px solid ${vars(locals.border_color)};
		background: ${vars(locals.background_color)};
		padding-block: ${vars(tokens.spacing_md)};
		padding-inline: ${vars(tokens.spacing_lg)};
		text-align: start;
		font-family: ${vars(tokens.font_family_body)};
		font-size: ${vars(tokens.text_size_label_default)};
		font-weight: ${vars(tokens.font_weight_regular)};
		line-height: ${vars(tokens.line_height_label_default)};
		min-height: ${vars(tokens.size_control_lg)};
	}

	.body {
		width: 100%;
		flex-grow: 1;
	}

	slot[name='start'] {
		display: flex;
		align-items: center;
		flex-shrink: 0;
	}

	:host([data-has-start='false']) slot[name='start'] {
		display: none;
	}

	/*
    1. Icons in the end-slot should have the secondary icon color.
  */
	slot[name='end'] {
		display: flex;
		align-items: center;
		flex-shrink: 0;
		transition: transform 125ms var(--ease-in);
		will-change: transform;
		--lfds--icon-color: ${vars(tokens.color_icon_secondary)}; /* [1] */
	}

	:host([data-has-end='false']) slot[name='end'] {
		display: none;
	}

	/*
	 * Loading — mask the real start/end slot content in place instead of
	 * guessing a placeholder shape. Only slots that are actually populated
	 * shimmer (default rounded-rect shape; shape per content type — e.g.
	 * circular avatars — is an open question, see DSR-012).
	 *
	 * Requires force-shimmer in addition to loading — by default,
	 * already-known content (e.g. an icon that's always present) stays
	 * visible while loading; force-shimmer opts into masking it anyway.
	 */
	:host([loading][force-shimmer]) slot[name='start'],
	:host([loading][force-shimmer]) slot[name='end'] {
		position: relative;
	}

	:host([loading][force-shimmer]) slot[name='start']::slotted(*),
	:host([loading][force-shimmer]) slot[name='end']::slotted(*) {
		visibility: hidden;
	}

	:host([loading][force-shimmer][data-has-start='true'])
		slot[name='start']::after,
	:host([loading][force-shimmer][data-has-end='true']) slot[name='end']::after {
		content: '';
		position: absolute;
		inset: 0;
		border-radius: var(--lfds-skeleton-border-radius);
		border: var(--lfds-skeleton-border);
		background: var(--lfds-skeleton-background);
		background-clip: padding-box;
		animation: var(--lfds-skeleton-animation);
	}

	.interactive {
		position: relative;
		cursor: pointer;
		/* Suppress the native Android/WebView blue tap flash so only our
		 * custom ::after state layer provides the press feedback. */
		-webkit-tap-highlight-color: transparent;
		transition: 100ms var(--ease-in);
		transition-property: background-color;

		> * {
			z-index: 1;
		}

		&::after {
			content: '';
			position: absolute;
			z-index: 0;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			background-color: ${vars(tokens.color_state_layer_on_light)};
			opacity: 0;
			pointer-events: none;
		}

		/* Keyboard focus — always show state layer */
		&:focus-visible::after {
			opacity: ${vars(tokens.effect_opacity_state_hover)};
			transition-timing-function: var(--ease-out);
		}

		/*
		 * Hover — only for true pointer devices.
		 * Without this guard, Android touch fires :hover on tap and never
		 * clears it (no mouseleave), leaving the state layer permanently visible.
		 */
		@media (hover: hover) {
			&:hover::after {
				opacity: ${vars(tokens.effect_opacity_state_hover)};
				transition-timing-function: var(--ease-out);
			}
		}

		/* Press feedback for touch (replaces hover feedback on coarse devices) */
		&:active::after {
			opacity: ${vars(tokens.effect_opacity_state_pressed)};
			transition-timing-function: var(--ease-out);
		}

		&:focus {
			outline: 0;
		}

		&.focus-visible:focus-visible {
			outline: var(--focus-outline);
			outline-offset: calc(var(--focus-outline-offset) * -1);
		}
	}

	/* =========================================================================
	 * Selectable row (renders as <div>, click forwarded via JS)
	 * ========================================================================= */

	/*
	 * Propagate hover to the slotted checkbox border color.
	 * Scoped to hover-capable devices — same reason as .interactive above.
	 */
	@media (hover: hover) {
		:host([selects]:hover) {
			${define(booleanControl, {
				border_color_control: vars(tokens.color_border_hover)
			})};
		}
	}

	.selectable {
		position: relative;
		cursor: pointer;
		user-select: none;
		/* Same as .interactive — prevent double-layer on Android WebView */
		-webkit-tap-highlight-color: transparent;

		> * {
			z-index: 1;
		}

		&::after {
			content: '';
			position: absolute;
			z-index: 0;
			inset: 0;
			background-color: ${vars(tokens.color_state_layer_on_light)};
			opacity: 0;
			pointer-events: none;
			transition: 100ms opacity var(--ease-in);
		}

		/* Hover — row state layer (pointer devices only) */
		@media (hover: hover) {
			&:hover::after {
				opacity: ${vars(tokens.effect_opacity_state_hover)};
				transition-timing-function: var(--ease-out);
			}
		}

		/* Press feedback for touch */
		&:active::after {
			opacity: ${vars(tokens.effect_opacity_state_pressed)};
			transition-timing-function: var(--ease-out);
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.interactive,
		.interactive::after,
		.selectable::after,
		slot[name='end'] {
			transition: none;
		}
	}
`
